import { useEffect } from "react";
import { useActiveWorkspace } from "@/domain/hooks";

/**
 * Writes the active workspace accent colour into CSS variables on the document
 * root, so every component bound to bg-brand / text-brand / border-brand
 * retints automatically when the workspace switches (spec §5.2).
 */
export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const workspace = useActiveWorkspace();

  useEffect(() => {
    const root = document.documentElement;
    const hsl = workspace.accentHsl; // "h s% l%"
    const alpha = workspace.accentSoftAlpha ?? 0.08;
    // Parse the triple so hover is a real lightness step (not an alpha blend that
    // washes out on white) and soft/line/ring are tinted from the same colour.
    const m = hsl.match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
    if (m) {
      const [h, s, l] = [Number(m[1]), Number(m[2]), Number(m[3])];
      root.style.setProperty("--accent-base", `hsl(${h} ${s}% ${l}%)`);
      root.style.setProperty("--accent-hover", `hsl(${h} ${s}% ${Math.max(0, l - 7)}%)`);
      root.style.setProperty("--accent-soft", `hsl(${h} ${s}% ${l}% / ${alpha})`);
      root.style.setProperty("--accent-line", `hsl(${h} ${s}% ${l}% / 0.22)`);
      root.style.setProperty("--accent-ring", `hsl(${h} ${s}% ${l}% / 0.4)`);
      root.style.setProperty("--accent-soft-alpha", String(alpha));
    } else {
      // Fallback to the raw triple if the format is unexpected.
      root.style.setProperty("--accent-base", `hsl(${hsl})`);
      root.style.setProperty("--accent-soft", `hsl(${hsl} / ${alpha})`);
    }
  }, [workspace]);

  return <>{children}</>;
}
